import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Fiber from "effect/Fiber";
import * as Scope from "effect/Scope";
import { EditorCapabilities } from "@linempo/application";
import { makeSession, type EditorSession } from "@linempo/editor-session";
import { acquireWorkerClient } from "@linempo/worker-client";

export interface EditorComposition {
  readonly ready: Promise<EditorSession>;
  /** Idempotent, including when React abandons an acquisition before it resolves. */
  readonly dispose: () => Promise<void>;
}

/** One call per React setup, with one root Scope for client and session.
 * LIFO release stops session policy fibers before the client closes native resources.
 */
export function createEditorComposition(): EditorComposition {
  const scope = Scope.makeUnsafe();
  const acquisition = Effect.runFork(Scope.provide(Effect.gen(function*() {
    const client = yield* acquireWorkerClient({
      createWorker: (kind) => kind === "preview"
        ? new Worker(new URL("../workers/preview.ts", import.meta.url), { type: "module" })
        : new Worker(new URL("../workers/export.ts", import.meta.url), { type: "module" }),
    });
    return yield* makeSession({ createId: () => crypto.randomUUID() }).pipe(Effect.provideService(EditorCapabilities, client));
  }), scope).pipe(Effect.onError(() => Scope.close(scope, Exit.void))));
  let disposal: Promise<void> | null = null;
  return {
    ready: Effect.runPromise(Fiber.join(acquisition)),
    dispose: () => disposal ??= Effect.runPromise(Effect.gen(function*() {
      // Closing a pending acquisition cannot wait for a future React render or Worker handshake.
      yield* Fiber.interrupt(acquisition);
      yield* Scope.close(scope, Exit.void);
    })),
  };
}
